import * as React from "react";
import { Check, X } from "lucide-react";

import { cn } from "@/lib/utils";

export interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?:
    | "default"
    | "primary"
    | "secondary"
    | "muted"
    | "destructive"
    | "success"
    | "warning"
    | "error";
  size?: "sm" | "md";
  shape?: "default" | "rounded";
  removable?: boolean;
  onRemove?: () => void;
  status?: "active" | "pending" | "failed";
  disabled?: boolean;
  toggled?: boolean;
  groupId?: string;
  isActive?: boolean;
  onGroupChange?: (groupId: string, chipIndex: number) => void;
  chipIndex?: number; // Add chipIndex for proper group management
}

const Chip = React.forwardRef<HTMLSpanElement, ChipProps>(
  (
    {
      className,
      variant = "default",
      size = "md",
      shape = "default",
      removable = false,
      onRemove,
      status,
      disabled = false,
      toggled,
      groupId,
      isActive,
      onGroupChange,
      chipIndex = 0, // Default to 0 if not provided
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const handleRemove = (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove?.();
    };

    const handleChipClick = (e: React.MouseEvent<HTMLSpanElement>) => {
      if (!disabled && toggled && !onClick) {
        // For toggled chips in a group, allow both selecting and deselecting
        if (groupId && onGroupChange) {
          // Use the chipIndex for proper group management
          onGroupChange(groupId, chipIndex);
        }
      }
      onClick?.(e);
    };

    // Determine the effective variant based on active state
    const effectiveVariant = toggled && isActive ? "primary" : variant;

    return (
      <span
        ref={ref}
        data-slot="chip"
        className={cn(
          "wl-chip",
          `wl-chip-${effectiveVariant}`,
          `wl-chip-${size}`,
          shape && `wl-chip-${shape}`,
          status && `wl-chip-status-${status}`,
          disabled && `wl-chip-disabled`,
          className
        )}
        onClick={handleChipClick}
        {...props}
      >
        {isActive && effectiveVariant === "primary" && <Check size={16} />}
        {status && (
          <div
            className={cn(
              "wl-chip-status-indicator",
              `wl-chip-status-indicator-${status}`
            )}
          />
        )}
        {children}
        {removable && (
          <button
            type="button"
            className="wl-chip-remove"
            onClick={handleRemove}
            aria-label="Remove chip"
          >
            <X className="wl-chip-remove-icon" />
          </button>
        )}
      </span>
    );
  }
);

Chip.displayName = "Chip";

// Export ChipGroup from the same file for convenience
export { ChipGroup } from "./chip-group";

export { Chip };
